// ============================================================
// THE FORCE FIELD'S RING, DRAWN (plan body-map T1; engine/plate/fieldRing).
// Every cell is drawn twice - once behind the figure, once before it - and
// the clock shows each in the half it is turning through (its other copy
// unseen): the cells move round the ring every frame, and an element cannot
// change its layer. The front copies glow.
// ============================================================

import type { Box } from '../../../../engine/plate/frame';
import { fieldRing, ringCells, type FieldMoment, type RingCell } from '../../../../engine/plate/fieldRing';
import tx from '../../../../styles/PlateTraits.module.css';
import type { WrittenNode } from '../scene';

/** The moment the still ring shows. */
const STILL_T = 1.7;

const EDGE: Record<RingCell['edge'], string> = {
  rest: 'var(--plate-field-ring)',
  flash: 'var(--plate-field-flare)',
  strain: 'var(--plate-field-strain)',
  gutter: 'var(--plate-field-gutter)',
};

const keyOf = (c: Pick<RingCell, 'column' | 'row'>, front: boolean) => `ff-${front ? 'f' : 'b'}${c.column}-${c.row}`;

/** A cell as drawn in its half: its place and look. */
const cellAttrs = (c: RingCell) => ({
  points: c.points,
  opacity: c.opacity,
  'fill-opacity': c.fillOpacity,
  stroke: EDGE[c.edge],
  'stroke-width': c.width,
});

/** What the clock writes of the ring at `t` under the attack's moment: each cell in its half, its other copy unseen. */
export function fieldNodes(rating: number, box: Box, t: number, m: FieldMoment): WrittenNode[] {
  return ringCells(fieldRing(box, rating), t, m).flatMap((c) => [
    { key: keyOf(c, c.front), attrs: cellAttrs(c) },
    { key: keyOf(c, !c.front), attrs: { opacity: 0 } },
  ]);
}

const QUIET: FieldMoment = { flash: 0, gutter: 0, scrambled: false, strain: false, shatter: 0 };

/** The ring's still form: as it stands at rest at the still moment. */
export const fieldStill = (rating: number, box: Box) => fieldNodes(rating, box, STILL_T, QUIET);

interface Props {
  u: string;
  /** The field's protection rating. */
  rating: number;
  box: Box;
}

/** One half of the ring: every cell's copy for this side, drawn as it stands at rest. */
function Half({ rating, box, front }: Omit<Props, 'u'> & { front: boolean }) {
  const cells = ringCells(fieldRing(box, rating), STILL_T, QUIET);
  return cells.map((c) => {
    const { points, opacity, stroke, ...rest } = cellAttrs(c);
    return (
      <polygon
        key={keyOf(c, front)}
        className={tx.fieldCell}
        data-g={keyOf(c, front)}
        points={points}
        opacity={c.front === front ? opacity : 0}
        stroke={stroke}
        fillOpacity={rest['fill-opacity']}
        strokeWidth={rest['stroke-width']}
      />
    );
  });
}

/** The ring's far half, behind the figure. */
export function FieldRingBack({ rating, box }: Omit<Props, 'u'>) {
  return (
    <g pointerEvents="none" data-trait="field" data-field={rating}>
      <Half rating={rating} box={box} front={false} />
    </g>
  );
}

/** The ring's near half, before the figure, glowing. */
export function FieldRingFront({ u, rating, box }: Props) {
  return (
    <g pointerEvents="none" filter={`url(#${u}-tglow)`}>
      <Half rating={rating} box={box} front />
    </g>
  );
}
